import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaUser, FaLock, FaRocket } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../../core/hooks/useAuth';
import { authApi } from './api/authApi';
import { AuroraBackground, ParticleBackground } from '../../shared/components';
import '../../pages/Login.css';

function Login() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await authApi.login(username, password);

      // Update auth context
      login(
        response.data.access_token,
        response.data.username,
        response.data.user.id,
        response.data.permissions
      );

      toast.success('Prihlásenie úspešné!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Chyba pri prihlásení');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Particle Background */}
      <ParticleBackground />

      {/* Aurora Effects */}
      <AuroraBackground />

      {/* Grid Overlay */}
      <div className="grid-overlay" />

      <div className="container">
        <motion.div
          className="glass-panel login-panel"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {/* Icon Container */}
          <motion.div
            className="icon-container login-icon"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <FaRocket />
          </motion.div>

          {/* Title */}
          <motion.h1
            className="login-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            GBLS UI
          </motion.h1>

          <motion.p
            className="login-subtitle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Prihláste sa do svojho účtu
          </motion.p>

          <motion.form
            onSubmit={handleSubmit}
            className="login-form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="input-group">
              <label className="form-label">
                <FaUser style={{ marginRight: '0.5rem' }} />
                Používateľské meno
              </label>
              <input
                type="text"
                placeholder="Zadajte používateľské meno"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="form-control"
              />
            </div>

            <div className="input-group">
              <label className="form-label">
                <FaLock style={{ marginRight: '0.5rem' }} />
                Heslo
              </label>
              <input
                type="password"
                placeholder="Zadajte heslo"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="form-control"
              />
            </div>

            <motion.button
              type="submit"
              className="btn-primary login-button"
              disabled={isLoading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isLoading ? (
                <>
                  <span className="loading-spinner"></span>
                  Prihlasovanie...
                </>
              ) : (
                'Prihlásiť sa'
              )}
            </motion.button>
          </motion.form>
        </motion.div>

        {/* Decorative Elements */}
        <motion.div
          className="login-decoration"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <p style={{
            textAlign: 'center',
            color: 'rgba(226, 232, 240, 0.5)',
            fontSize: '0.9rem',
            marginTop: '2rem'
          }}>
            Powered by GBLS Technology
          </p>
        </motion.div>
      </div>
    </div>
  );
}

export default Login;
